import { useMemo } from 'react';
import { format } from '@visx/vendor/d3-format';
import { utcFormat } from '@visx/vendor/d3-time-format';

import {
    STUDIO_EXECUTION_COL_TYPE_MAP,
    StudioExecutionColumnTypes,
} from '@blockworks/platform/api/research/studio/execution';
import { StringUnion } from '@blockworks/platform/typescript';

import { replaceGiga } from '../../visualization';

const getColor = (value?: string | number, hasPositiveColor?: boolean, hasNegativeColor?: boolean) => {
    let safeValue = value as string;
    if (typeof value !== 'string') {
        safeValue = value?.toString() ?? '';
    }
    if (hasNegativeColor && +safeValue < 0) {
        return 'danger' as const;
    }
    if (hasPositiveColor && +safeValue > 0) {
        return 'success' as const;
    }
    return undefined;
};

type D3Formats = StringUnion<'%m-%d-%Y' | '%m-%d-%Y %H:%M:%S' | '%m-%d-%Y %H:%M:%S.%L' | '.0%' | '$.2f' | ','>;

type D3FormatString = StringUnion<
    | D3Formats
    | `{${string}}${D3Formats}{${string}}`
    | `${D3Formats}{${string}}`
    | `{${string}}${D3Formats}`
    | `{${string}}`
>;

type UseFormatValueProps = {
    value?: string | number;
    d3Format?: D3FormatString;
    type?: StudioExecutionColumnTypes;
    hasPositiveColor?: boolean;
    hasNegativeColor?: boolean;
};

const formatValue = (value?: string | number, d3Format?: D3FormatString, colType?: StudioExecutionColumnTypes) => {
    let formattedValue = value;
    if (!d3Format || !colType || value === undefined || value === null) return formattedValue;

    const type = STUDIO_EXECUTION_COL_TYPE_MAP[colType];

    const parts: string[] = [];
    const pattern = d3Format.replace(/\{([^}]+)\}/g, (_, content) => {
        /** store `{}` wrapped content in parts (prefix, suffix) array */
        parts.push(content);
        return '';
    });

    try {
        if (type === 'number') {
            const formatNumber = format(pattern);
            formattedValue = replaceGiga(formatNumber(+value));
        }

        if (type === 'date') {
            const formatDate = utcFormat(pattern);
            formattedValue = formatDate(new Date(value));
        }

        let placeholderIndex = 0;
        formattedValue = d3Format
            .replace(/\{([^}]+)\}/g, () => {
                /** replace each placeholder with the extracted content */
                return parts[placeholderIndex++] as string;
            })
            /** replace the leftover d3 pattern with the formatted value */
            .replace(pattern, formattedValue?.toString() ?? '');
    } catch (_) {
        formattedValue = value;
    }
    return formattedValue;
};

const useFormatValue = ({ value, d3Format, type, hasPositiveColor, hasNegativeColor }: UseFormatValueProps) => {
    return useMemo(() => {
        const formattedValue = formatValue(value, d3Format, type);

        return {
            formattedValue,
            color: getColor(value, hasPositiveColor, hasNegativeColor),
        };
    }, [d3Format, value, type, hasPositiveColor, hasNegativeColor]);
};

export type { D3FormatString, UseFormatValueProps };
export { formatValue, useFormatValue };
